<script setup>
import { onBeforeUnmount, onMounted } from 'vue'
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import SalesOrderListButton from '@core/components/SalesOrderListButton.vue'
import initCore from '@core/initCore'
import {
  initConfigStore,
  useConfigStore,
} from '@core/stores/config'
import { hexToRgb } from '@layouts/utils'
import axios from 'axios'
import { useCookie } from '@core/composable/useCookie'
import PwaPrompts from '@/components/PwaPrompts.vue'


const { global } = useTheme()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()

const tableWrapperSelector = '.v-data-table .v-table__wrapper, .mobile-table-wrapper'
let tableWrapperObserver = null
let activeTableTouch = null

const markTableWrappers = () => {
  if (typeof document === 'undefined') return

  document.querySelectorAll('.v-data-table .v-table__wrapper').forEach(el => {
    el.classList.add('mobile-table-wrapper')
  })
}

const handleTouchStartCapture = event => {
  const wrapper = event.target?.closest?.(tableWrapperSelector)
  if (!wrapper) {
    activeTableTouch = null
    return
  }

  const touch = event.touches?.[0]
  if (!touch) return

  activeTableTouch = {
    wrapper,
    startX: touch.clientX,
    startY: touch.clientY,
    lastX: touch.clientX,
  }

  event.stopPropagation()
}

const handleTouchMoveCapture = event => {
  if (!activeTableTouch) return

  const touch = event.touches?.[0]
  if (!touch) return

  const deltaX = touch.clientX - activeTableTouch.startX
  const deltaY = touch.clientY - activeTableTouch.startY
  const absX = Math.abs(deltaX)
  const absY = Math.abs(deltaY)

  if (absX > absY && activeTableTouch.wrapper) {
    const moveX = touch.clientX - activeTableTouch.lastX
    activeTableTouch.wrapper.scrollLeft -= moveX
    activeTableTouch.lastX = touch.clientX
    event.stopPropagation()
    event.preventDefault()
  }
}

const clearTouchState = () => {
  activeTableTouch = null
}

onMounted(() => {
  markTableWrappers()

  if (typeof MutationObserver !== 'undefined') {
    tableWrapperObserver = new MutationObserver(() => {
      markTableWrappers()
    })
    tableWrapperObserver.observe(document.body, { childList: true, subtree: true })
  }

  document.addEventListener('touchstart', handleTouchStartCapture, { passive: true, capture: true })
  document.addEventListener('touchmove', handleTouchMoveCapture, { passive: false, capture: true })
  document.addEventListener('touchend', clearTouchState, { passive: true, capture: true })
  document.addEventListener('touchcancel', clearTouchState, { passive: true, capture: true })

  // Fetch and cache settings
  try {
    const config = { headers: { 'Authorization': `Bearer ${useCookie('accessToken').value}` } };
    axios.get('/api/settings', config).then(res => {
      localStorage.setItem('whatsapp_message_sending', res.data.whatsapp_message_sending);
      localStorage.setItem('whatsapp_status_bot', res.data.whatsapp_status_bot);
    }).catch(e => console.error(e));
  } catch(e) {}
})

onBeforeUnmount(() => {
  tableWrapperObserver?.disconnect()
  tableWrapperObserver = null

  document.removeEventListener('touchstart', handleTouchStartCapture, { capture: true })
  document.removeEventListener('touchmove', handleTouchMoveCapture, { capture: true })
  document.removeEventListener('touchend', clearTouchState, { capture: true })
  document.removeEventListener('touchcancel', clearTouchState, { capture: true })
})


</script>
<style>

    .v-field__input{
      overflow: hidden!important;
    }

    .v-table{
      font-size: 14px!important;
    }

    .v-field{
      color:gainsboro!important;
    }
  
    .Vue-Toastification__toast{
      display: inline-flex!important;
        position: relative!important;
        max-height: 64px!important;
        min-height: 54px!important;
        box-sizing: border-box!important;
        margin-bottom: 1rem!important;
        padding: 5px !important;
        border-radius: 8px!important;
        box-shadow: 0 1px 10px 0 rgba(0, 0, 0, 0.1), 0 2px 15px 0 rgba(0, 0, 0, 0.05)!important;
        justify-content: space-between!important;
        font-family: "Lato", Helvetica, "Roboto", Arial, sans-serif!important;
        max-width: 600px!important;
        min-width: 326px!important;
        pointer-events: auto!important;
        overflow: hidden!important;
        transform: translateZ(0)!important;
        direction: ltr!important;
    }

    .Vue-Toastification__toast-body{
      margin-top: 10px;
    }

    .Vue-Toastification__icon{
      margin: auto 9px auto 8px!important;
    }

    .Vue-Toastification__close-button{
      display: none;
    }

    .v-table>.v-table__wrapper>table>thead>tr>th{
      text-align: center !important;
      font-weight: 800!important;
    }

    /* Fit tables to the card width on desktop only. On smaller screens a
       fixed layout squeezes every column to a few pixels (headers wrap one
       letter per line), so there tables keep their natural column widths and
       scroll horizontally (see styles.scss + the touch handler above). */
    @media (min-width: 960px) {
      .v-table > .v-table__wrapper,
      .v-data-table .v-table__wrapper,
      .mobile-table-wrapper {
        overflow-x: hidden !important;
      }

      .v-table > .v-table__wrapper > table,
      .v-data-table .v-table__wrapper > table,
      .mobile-table-wrapper > table {
        width: 100% !important;
        min-width: 0 !important;
        table-layout: fixed !important;
      }

      .v-table > .v-table__wrapper > table > thead > tr > th,
      .v-table > .v-table__wrapper > table > tbody > tr > td,
      .v-data-table .v-table__wrapper > table > thead > tr > th,
      .v-data-table .v-table__wrapper > table > tbody > tr > td,
      .mobile-table-wrapper > table > thead > tr > th,
      .mobile-table-wrapper > table > tbody > tr > td {
        white-space: normal !important;
        overflow-wrap: anywhere;
        word-break: break-word;
        min-width: 0;
      }
    }

    @media (max-width: 959.98px) {
      .v-table > .v-table__wrapper,
      .v-data-table .v-table__wrapper,
      .mobile-table-wrapper {
        overflow-x: auto !important;
      }
    }

    .v-table > .v-table__wrapper > table > thead > tr > th,
    .v-table > .v-table__wrapper > table > tbody > tr > td,
    .v-data-table .v-table__wrapper > table > thead > tr > th,
    .v-data-table .v-table__wrapper > table > tbody > tr > td,
    .mobile-table-wrapper > table > thead > tr > th,
    .mobile-table-wrapper > table > tbody > tr > td {
      vertical-align: middle !important;
    }

    .v-table > .v-table__wrapper > table > thead > tr > th,
    .v-data-table .v-table__wrapper > table > thead > tr > th,
    .mobile-table-wrapper > table > thead > tr > th {
      line-height: 1.2 !important;
      padding: 8px 8px !important;
    }

    .v-table > .v-table__wrapper > table > tbody > tr > td,
    .v-data-table .v-table__wrapper > table > tbody > tr > td,
    .mobile-table-wrapper > table > tbody > tr > td {
      padding: 8px 8px !important;
    }

    /* Ensure chips and controls inside table cells are fully visible */
    .v-table > .v-table__wrapper > table > tbody > tr > td,
    .v-data-table .v-table__wrapper > table > tbody > tr > td,
    .mobile-table-wrapper > table > tbody > tr > td {
      overflow: visible !important;
    }

    .v-data-table .v-chip,
    .v-table .v-chip {
      white-space: nowrap !important;
      display: inline-flex !important;
    }

    /* Make the Status column wider so VChip text isn't clipped
       (Status is the 12th column in the embparty table headers) */
    .v-table > .v-table__wrapper > table > thead > tr > th:nth-child(12),
    .v-data-table .v-table__wrapper > table > thead > tr > th:nth-child(12),
    .v-table > .v-table__wrapper > table > tbody > tr > td:nth-child(12),
    .v-data-table .v-table__wrapper > table > tbody > tr > td:nth-child(12),
    /* Select-tab shifts columns by one (checkbox column) — include 13th as well */
    .v-table > .v-table__wrapper > table > thead > tr > th:nth-child(13),
    .v-data-table .v-table__wrapper > table > thead > tr > th:nth-child(13),
    .v-table > .v-table__wrapper > table > tbody > tr > td:nth-child(13),
    .v-data-table .v-table__wrapper > table > tbody > tr > td:nth-child(13) {
      min-width: 140px !important;
      white-space: nowrap !important;
    }
    /* Allow the chip inside Status column to wrap onto two lines and center it */
    .v-table > .v-table__wrapper > table > tbody > tr > td:nth-child(12) .v-chip,
    .v-data-table .v-table__wrapper > table > tbody > tr > td:nth-child(12) .v-chip,
    /* Status in select-tab is 13th column */
    .v-table > .v-table__wrapper > table > tbody > tr > td:nth-child(13) .v-chip,
    .v-data-table .v-table__wrapper > table > tbody > tr > td:nth-child(13) .v-chip {
      white-space: normal !important;
      max-width: 140px !important;
      text-align: center !important;
      padding-left: 10px !important;
      padding-right: 10px !important;
      display: inline-flex !important;
      justify-content: center !important;
      align-items: center !important;
      line-height: 1.1 !important;
    }

    .v-select .v-select__selection:first-child {
        margin-inline-start: 12px!important;
    }
    
    
  .row-yellow {
    background-color: #e3b413 !important; /* soft yellow */
  }

  .row-green {
    background-color: #d4edda !important; /* soft green */
  }

  .row-blue {
    background-color: #e3f2fd !important; /* soft blue */
  }

  .job-row-embroidery,
  .job-row-embroidery > td,
  .job-row-embroidery > th {
    background-color: #D4EDDA !important;
  }

  .job-row-jacquard,
  .job-row-jacquard > td,
  .job-row-jacquard > th {
    background-color: #E3B413 !important;
  }

  .job-row-sample,
  .job-row-sample > td,
  .job-row-sample > th {
    background-color: #FFB1B3 !important;
  }

  .job-row-urgent,
  .job-row-urgent > td,
  .job-row-urgent > th {
    background-color: #FFF1DF !important;
  }

  .job-row-cancel,
  .job-row-cancel > td,
  .job-row-cancel > th {
    background-color: #e0e0e0 !important;
  }

  .layout-page-content{
    padding-inline: 0px !important;
  }

  /* With no page padding, a page-level VRow's -12px gutters stick out past
     the viewport and cause a horizontal scroll on every screen size. */
  .page-content-container > .v-row,
  .v-card > .v-row {
    margin-inline: 0;
  }

  /* Same for rows inside the 8px-padded card text used by the list-page
     toolbars (page size + search): keep them inside the card edges. */
  .v-card-text.pa-2 > .v-row {
    margin-inline: -8px;
  }

  .vel-btns-wrapper .btn__prev {
      left: 20%!important;
  }

  /* Caption inside lightbox */
  .custom-caption {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    background-color: rgba(0,0,0,0.6);
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 16px;
    pointer-events: none;
    z-index: 9999;
  }
</style>
<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />

      <ScrollToTop />

      <SalesOrderListButton />

      <PwaPrompts />
    </VApp>
  </VLocaleProvider>
</template>
